import i18next from 'i18next';
import { find, findKey, isEmpty, isNull } from 'lodash';
import { Trans } from 'react-i18next';
import moment from 'moment';
import { theme } from 'style/globalStyle';
import { getCode } from 'country-list';
import { ERROR } from './Constants';
import { getCountryName as getCountryNameFromUtils } from './Utils';

export const canReevaluate = (evData, onboarded) => {
  return (
    onboarded &&
    (!evData ||
      hasNoData(evData) ||
      isEvDataSharedButExpired(evData) ||
      [
        EcovadisSharingStatus.NA,
        EcovadisSharingStatus.DECLINED_NO_RESPONSE,
        EcovadisSharingStatus.DECLINED_REFUSED_TO_PARTICIPATE,
        EcovadisSharingStatus.CANCELED_BY_CAMPAIGN_USER,
      ].includes(getEvDataScopeChangeFieldValue(evData, 'sharing_status_code')))
  );
};

export const isEvDataSharedButExpired = (evData) => {
  return (
    getEvDataScopeChangeFieldValue(evData, 'sharing_status_code') ===
    EcovadisSharingStatus.ACCEPTED && isEvDataExpired(evData)
  );
};

export const isEvDataExpired = (evData) => {
  const isExpirationDateBeforeToday =
    getEvDataScopeChangeFieldValue(evData, 'published_date') &&
    moment(getEvDataScopeChangeFieldValue(evData, 'published_date'))
      .add(1, 'y')
      .isBefore(moment());
  return evData?.expired || isExpirationDateBeforeToday;
};

export const isEvDataAccepedButExpired = (evData) => {
  return (
    getEvDataScopeChangeFieldValue(evData, 'sharing_status_code') ===
    EcovadisSharingStatus.ACCEPTED && evData.expired
  );
};

export const hasNoData = (evData) => {
  return (
    isEmptyEvData(evData) ||
    getEvDataScopeChangeFieldValue(evData, 'sharing_status_code') === 0
  );
};

export const showSpecificSharingMessage = (evData) => {
  return [
    EcovadisSharingStatus.DECLINED_NO_RESPONSE,
    EcovadisSharingStatus.DECLINED_REFUSED_TO_PARTICIPATE,
    EcovadisSharingStatus.CANCELED_BY_CAMPAIGN_USER,
    EcovadisSharingStatus.CANCELED_BY_ECOVADIS,
  ].includes(getEvDataScopeChangeFieldValue(evData, 'sharing_status_code'));
};

export const shouldDisplayEvSharingMessageBloc = (evData) => {
  return (
    !evData ||
    [
      EcovadisSharingStatus.NA,
      EcovadisSharingStatus.DECLINED_NO_RESPONSE,
      EcovadisSharingStatus.DECLINED_REFUSED_TO_PARTICIPATE,
      EcovadisSharingStatus.CANCELED_BY_CAMPAIGN_USER,
      EcovadisSharingStatus.CANCELED_BY_ECOVADIS,
    ].includes(getEvDataScopeChangeFieldValue(evData, 'sharing_status_code'))
  );
};

export const EcovadisEvaluationStep = {
  INTEGRATION_ID: 'INTEGRATION_ID',
  CONCERNED_SUPPLIER: 'CONCERNED_SUPPLIER',
  SUPPLIER_FORM: 'SUPPLIER_FORM',
  BUYER_FORM: 'BUYER_FORM',
};

export const EcovadisCurrentStageProgressSteps = {
  REGISTRATION: 'registration',
  QUESTIONNAIRE: 'questionnaire',
  EXPERT_ANALYSIS: 'analysis',
  RESULTS: 'results',
};

export const EcovadisSharingProgressSteps = {
  REQUESTED: 'requested',
  ACCEPTED: 'accepted'
};

export const EcovadisSharingStatus = {
  REQUESTED: 1,
  ACCEPTED: 2,
  DECLINED_NO_RESPONSE: 3,
  DECLINED_REFUSED_TO_PARTICIPATE: 4,
  CANCELED_BY_CAMPAIGN_USER: 5,
  CANCELED_BY_ECOVADIS: 6,
  NA: 0,
};

export const EcovadisCurrentStageStatus = {
  REGISTRATION: 1,
  QUESTIONNAIRE: 2,
  EXPERT_ANALYSIS: 3,
  SHARING_PENDING: 4,
  QUEUED: 5,
  NOT_UNDER_ASSESSMENT: 6,
  QUESTIONNAIRE_NOT_COMPANYS_REQUEST: 7,
  EXPERT_ANALYSIS_NOT_COMPANYS_REQUEST: 8,
  NA: 0,
};

export const isEcovadisCurrentStageProgressStepActive = (step, evData) => {
  if (!evData || !getEvDataScopeChangeFieldValue(evData, 'current_stage_code'))
    return false;

  switch (getEvDataScopeChangeFieldValue(evData, 'current_stage_code')) {
    case EcovadisCurrentStageStatus.REGISTRATION:
    case EcovadisCurrentStageStatus.QUEUED:
      return step === EcovadisCurrentStageProgressSteps.REGISTRATION;

    case EcovadisCurrentStageStatus.QUESTIONNAIRE:
    case EcovadisCurrentStageStatus.QUESTIONNAIRE_NOT_COMPANYS_REQUEST:
      return step === EcovadisCurrentStageProgressSteps.QUESTIONNAIRE;

    case EcovadisCurrentStageStatus.EXPERT_ANALYSIS:
    case EcovadisCurrentStageStatus.EXPERT_ANALYSIS_NOT_COMPANYS_REQUEST:
      return step === EcovadisCurrentStageProgressSteps.EXPERT_ANALYSIS;

    case EcovadisCurrentStageStatus.SHARING_PENDING:
      return step === EcovadisCurrentStageProgressSteps.RESULTS;

    case EcovadisCurrentStageStatus.NOT_UNDER_ASSESSMENT:
    case EcovadisCurrentStageStatus.NA:
    default:
      return false;
  }
};

export const isEcovadisSharingProgressStepActive = (step, evData) => {
  if (!evData || !getEvDataScopeChangeFieldValue(evData, 'sharing_status_code'))
    return false;

  switch (getEvDataScopeChangeFieldValue(evData, 'sharing_status_code')) {
    case EcovadisSharingStatus.REQUESTED:
      return step === EcovadisSharingProgressSteps.REQUESTED;
    case EcovadisSharingStatus.ACCEPTED:
      return step === EcovadisSharingProgressSteps.ACCEPTED;
    default:
      return false;
  }
};

export const isEcovadisCurrentStageProgressStepDone = (step, evData) => {
  if (!evData || !getEvDataScopeChangeFieldValue(evData, 'current_stage_code'))
    return false;

  switch (step) {
    case EcovadisCurrentStageProgressSteps.REGISTRATION:
      return [
        EcovadisCurrentStageStatus.QUESTIONNAIRE,
        EcovadisCurrentStageStatus.QUESTIONNAIRE_NOT_COMPANYS_REQUEST,
        EcovadisCurrentStageStatus.EXPERT_ANALYSIS,
        EcovadisCurrentStageStatus.EXPERT_ANALYSIS_NOT_COMPANYS_REQUEST,
        EcovadisCurrentStageStatus.SHARING_PENDING,
      ].includes(getEvDataScopeChangeFieldValue(evData, 'current_stage_code'));

    case EcovadisCurrentStageProgressSteps.QUESTIONNAIRE:
      return [
        EcovadisCurrentStageStatus.EXPERT_ANALYSIS,
        EcovadisCurrentStageStatus.EXPERT_ANALYSIS_NOT_COMPANYS_REQUEST,
        EcovadisCurrentStageStatus.SHARING_PENDING,
      ].includes(getEvDataScopeChangeFieldValue(evData, 'current_stage_code'));

    case EcovadisCurrentStageProgressSteps.EXPERT_ANALYSIS:
      return (
        getEvDataScopeChangeFieldValue(evData, 'current_stage_code') ===
        EcovadisCurrentStageStatus.SHARING_PENDING
      );

    case EcovadisCurrentStageProgressSteps.RESULTS:
    default:
      return false;
  }
};


export const isEcovadisSharingProgressStepDone = (step, evData) => {
  if (!evData || !getEvDataScopeChangeFieldValue(evData, 'sharing_status_code'))
    return false;

  switch (step) {
    case EcovadisSharingProgressSteps.REQUESTED:
      return [
        EcovadisSharingStatus.REQUESTED,
        EcovadisSharingStatus.ACCEPTED,
        EcovadisSharingStatus.DECLINED_NO_RESPONSE,
        EcovadisSharingStatus.DECLINED_REFUSED_TO_PARTICIPATE,
        EcovadisSharingStatus.CANCELED_BY_CAMPAIGN_USER,
        EcovadisSharingStatus.CANCELED_BY_ECOVADIS,
      ].includes(getEvDataScopeChangeFieldValue(evData, 'sharing_status_code'));

    case EcovadisSharingProgressSteps.ACCEPTED:
      return getEvDataScopeChangeFieldValue(evData, 'sharing_status_code') === EcovadisSharingStatus.ACCEPTED

    default:
      return false;
  }
};

export const isEmptyEvData = (evData) => {
  return !evData || Object.values(evData).every(isNull);
};

export const isEvScoresAvailable = (evData) => {
  return (
    getEvDataScopeChangeFieldValue(evData, 'sharing_status_code') ===
    EcovadisSharingStatus.ACCEPTED && !isEmpty(evData?.scorecard_link)
  );
};

export const isNewEvaluation = (evData) => {
  return getEvDataScopeChangeFieldValue(evData, 'current_stage_code') === null && 
    getEvDataScopeChangeFieldValue(evData, 'sharing_status_code') === null;
}

export const isEvEvaluationInProgress = (evData) => {
  return (
    evData && 
    getEvDataScopeChangeFieldValue(evData, 'current_stage_code') !== EcovadisCurrentStageStatus.NA &&
    getEvDataScopeChangeFieldValue(evData, 'current_stage_code') !== EcovadisCurrentStageStatus.NOT_UNDER_ASSESSMENT
  )
};

export const isEvSharingInProgress = (evData) => {
  return (
    evData &&
    (
      getEvDataScopeChangeFieldValue(evData, 'sharing_status_code') === EcovadisSharingStatus.REQUESTED ||
      !isEvScoresAvailable(evData)
    )
  );
};

export const getCurrentStageMessage = (currentStageCode, validator) => {
  if (currentStageCode === 3) {
    return (
      <Trans i18nKey="ecovadis.messages.currentStage.3" values={{ validator }}>
        X <b>{validator}</b> Y.
      </Trans>
    );
  } else if (currentStageCode !== null && currentStageCode !== undefined) {
    return i18next.t(`ecovadis.messages.currentStage.${currentStageCode}`);
  }
  return i18next.t(`ecovadis.messages.currentStage.1`);
};

export const getSharingMessage = (sharingStatusCode) => {
  if (sharingStatusCode !== null && sharingStatusCode !== undefined) {
    return i18next.t(`ecovadis.messages.sharing.${sharingStatusCode}`);
  } else {
    return i18next.t(`ecovadis.messages.sharing.1`);
  }
}

export const EvScores = Object.freeze([
  'env_score',
  'lab_score',
  'fbp_score',
  'sup_score',
]);

export const EvAllScores = Object.freeze(['global_score', ...EvScores]);

export const EvAllScoresPrefixed = EvAllScores.map(score => `ecovadis_${score}`);

export const EcovadisScoresEnum = Object.freeze({
  GLOBAL: 'global_score',
  ENV: 'env_score',
  LAB: 'lab_score',
  FBP: 'fbp_score',
  SUP: 'sup_score',
})

export const EVScorePerfomance = Object.freeze({
  OUTSTANDING: 'outstanding',
  ADVANCED: 'advanced',
  GOOD: 'good',
  PARTIAL: 'partial',
  INSUFFICIENT: 'insufficient',
});


export const EVAllScorePerfomance = Object.freeze({
  NOT_PUBLISHED: 'not_published',
  ...EVScorePerfomance
});

export const EcovadisPerformanceOrder = Object.freeze({
  [EVAllScorePerfomance.OUTSTANDING]: 1,
  [EVAllScorePerfomance.ADVANCED]: 2,
  [EVAllScorePerfomance.GOOD]: 3,
  [EVAllScorePerfomance.PARTIAL]: 4,
  [EVAllScorePerfomance.INSUFFICIENT]: 5,
  [EVAllScorePerfomance.NOT_PUBLISHED]: 6,
});

export const getScorePerformanceByValue = (value) => {
  if (value >= 85) {
    return EVScorePerfomance.OUTSTANDING;
  }
  if (value >= 65) {
    return EVScorePerfomance.ADVANCED;
  }
  if (value >= 45) {
    return EVScorePerfomance.GOOD;
  }
  if (value >= 25) {
    return EVScorePerfomance.PARTIAL;
  }
  return EVScorePerfomance.INSUFFICIENT;
};

export const getEvDataScopeChangeFieldValue = (
  evData,
  fieldName,
  targetFieldName
) => {
  return evData?.scope_change === true
    ? evData?.[targetFieldName || `target_${fieldName}`]
    : evData?.[fieldName];
};

export const getEcovadisPerformanceGraphColors = (scorePerformance) => {
  switch (scorePerformance) {
    case EVScorePerfomance.OUTSTANDING:
      return {
        bullet: {
          fill: '#D5ECD9',
          stroke: EcovadisPerformanceColor[scorePerformance],
        },
        tooltip: {
          textColor: theme.palette.primary.primary300,
          fill: '#42523B',
        },
      };
    case EVScorePerfomance.ADVANCED:
      return {
        bullet: {
          fill: '#E2F7E5',
          stroke: EcovadisPerformanceColor[scorePerformance],
        },
        tooltip: {
          textColor: theme.palette.primary.primary200,
          fill: '#518339',
        },
      };
    case EVScorePerfomance.GOOD:
      return {
        bullet: {
          fill: '#F4FBCC',
          stroke: EcovadisPerformanceColor[scorePerformance],
        },
        tooltip: {
          textColor: EcovadisPerformanceColor[scorePerformance],
          fill: '#849727',
        },
      };
    case EVScorePerfomance.PARTIAL:
      return {
        bullet: {
          fill: '#FFECCF',
          stroke: EcovadisPerformanceColor[scorePerformance],
        },
        tooltip: {
          textColor: EcovadisPerformanceColor[scorePerformance],
          fill: '#84773F',
        },
      };
    case EVScorePerfomance.INSUFFICIENT:
    default:
      return {
        bullet: {
          fill: theme.palette.error.error200,
          stroke: EcovadisPerformanceColor[scorePerformance],
        },
        tooltip: {
          textColor: theme.palette.error.error200,
          fill: '#872128',
        },
      };
  }
};

export const EcovadisPerformanceColor = Object.freeze({
  [EVAllScorePerfomance.OUTSTANDING]: '#436328',
  [EVAllScorePerfomance.ADVANCED]: '#61C424',
  [EVAllScorePerfomance.GOOD]: '#C7EC00',
  [EVAllScorePerfomance.PARTIAL]: '#E7C628',
  [EVAllScorePerfomance.INSUFFICIENT]: '#D75E47',
  [EVAllScorePerfomance.NOT_PUBLISHED]: theme.palette.grey.grey400,
});

export const EVSupplierGeneralIdentifiers = Object.freeze({
  VAT_NUMBER: 'vat_number',
  TAX_NUMBER: 'tax_number',
  SIRET_NUMBER: 'siret_number',
});

export const EVSupplierGeneral = Object.freeze({
  EV_SUPPLIER_NAME: 'ev_supplier_name',
  CLIENT_SUPPLIER_NAME: 'client_supplier_name',
  PARENT_COMPANY: 'parent_company',
  ACTIVE: 'active',
  EVID: 'evid',
  ...EVSupplierGeneralIdentifiers,
});

export const EVSupplierAdditionalAddress = Object.freeze({
  ADDRESS_1: 'address_1',
  ADDRESS_2: 'address_2',
  CITY: 'city',
  STATE: 'state',
  COUNTRY: 'country',
});

export const EVSupplierAdditionalInformations = Object.freeze({
  WEBSITE: 'website',
  EMPLOYEE_RANGE: 'employee_range',
  SIZE: 'size',
  TURNOVER: 'turnover',
});

export const EVSupplierAdditional = Object.freeze({
  ...EVSupplierAdditionalAddress,
  ...EVSupplierAdditionalInformations,
  ACTIVITY: 'isic_category',
  RISK_COUNTRY: 'risk_country',
});

// Returns true if at least one value of the given keys from evData is not empty
export const hasOneNotEmptyValue = (evData, keyArr) => {
  return keyArr?.some((key) => !isEmpty(evData?.[key]));
};

export const getEcovadisThirdpartyIntegrationID = (thirdpartyId) => {
  return "APROVALL_" + thirdpartyId
}

export const getCountryName = (supplierCountryName) => {
  if (!supplierCountryName) return '';
  const countryCode = getCode(supplierCountryName);
  return countryCode
    ? getCountryNameFromUtils(countryCode, i18next.language)
    : supplierCountryName;
};

export const EcovadisEapErrors = Object.freeze({
  E700: 'E700', // Multiple results for Integration ID.
  E701: 'E701', // EVID already used for other thirdparty.
  E702: 'E702', // IntegrationID returns no result for process.
  E703: 'E703', // IntegrationID returns no result.
  E704: 'E704', // EVID doesn't match the thirdparty's already saved EVID.
  E705: 'E705', // Call limit.
});

export const isMultipleSuppliersError = (evData) => evData?.error === EcovadisEapErrors.E700;

export const splitMultipleSuppliers = (evData) => {
  return evData?.error_message?.split(";;")
}

export const hasEcovadisError = (evData) => {
  return evData?.error && evData?.error !== EcovadisEapErrors.E705 // we shouldn't show the panel if it's call limit error.
};

export const handleEcovadisApiCallError = (error, thirdpartyId, dispatch = () => { }) => {
  const errMsgCode = error.message?.messages?.error?.[0]?.code;

  const isEcovadisError = Object.values(EcovadisEapErrors).includes(errMsgCode);

  const errorMsg = isEcovadisError
    ? i18next.t(`ecovadis.messages.errors.${errMsgCode}`, {integrationId: getEcovadisThirdpartyIntegrationID(thirdpartyId)})
    : i18next.t('infos.INTERNAL_ERROR');

  dispatch({
    type: 'ALERT',
    alert: { type: ERROR, msg: errorMsg },
  });
}

export const getCultureString = (languageCode) => {
  const cultureMap = {
    fr: "fr-FR",
    it: "it-IT",
    en: "en-GB",
    nl: "nl-NL",
    es: "es-ES",
    zh: "zh-CN",
    de: "de-DE",
    pt: "pt-BR",
    ja: "ja-JP",
    ru: "ru-RU",
    th: "th-TH",
    ko: "ko-KR",
    tr: "tr-TR",
    ar: "ar-001"
  };

  return cultureMap[languageCode] || "en-GB";
}

export const splitEcovadisFiltersByScore = (filters) => {
  let ecovadisFilters = filters?.reduce((res, filter) => {
    const evScore = filter.type?.substring(9)
    const scoreObj = find(res, { score: evScore });
    const value = findKey(EVAllScorePerfomance, (value) => value === filter.id);

    // Add a new score object if it doesnt exist.
    if (!scoreObj) {
      res.push({ score: evScore, values: [value] })
    } else {
      // or add the value to its existing filter values.
      scoreObj.values.push(value)
    }
    return res;
  }, []);

  // Add remaining scores with empty values if they don't already exist
  const orderedFilters = [];
  EvAllScores.forEach((score) => {
    const scoreFilter = ecovadisFilters.find((filter) => filter.score === score);
    orderedFilters.push(scoreFilter || { score, values: [] });
  });

  return orderedFilters;
}
